import { useEffect, useState, useRef } from 'react';
import { connect } from 'redux-bundler-react';

import { MapContainer, TileLayer, FeatureGroup, LayersControl, useMap, useMapEvents, Marker } from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import * as turf from '@turf/turf';

import './map.scss';
import { useFormContext } from 'react-hook-form';
import TextInput from '@components/text-input/TextInput';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import icon from 'leaflet/dist/images/marker-icon.png';
import 'leaflet-sleep';
import FullscreenControl from './components/fullScreenControl';

import { latRegex, lngRegex } from '../../utils/regex.jsx';
import { formatCoordStr } from '../../utils/helpers.jsx';
import { leafletAttribution } from './helper.jsx';

import useRequest from '@stores/request.store.js';

const DefaultIcon = L.icon({
  iconUrl: icon,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  shadowAnchor: [4, 62],
  popupAnchor: [-3, -76],
});

L.Marker.prototype.options.icon = DefaultIcon;

const PermitMap = connect('selectRequestFormData', ({ requestFormData, handleChange = () => {}, height = '60vh' }) => {
  const zoom = requestFormData?.location?.latitude ? 10 : 4;

  const { setValue, watch } = useFormContext();
  const isReadOnly = useRequest((state) => state.isFormReadOnly);

  const latitude = watch('dredgeDisposalLatitude');
  const longitude = watch('dredgeDisposalLongitude');

  const [projectBounds, setProjectBounds] = useState();
  const [projectBoundaryGeometry, setProjectBoundaryGeometry] = useState(() => {
    //Ensuring the projectBoundary has the most up to date coordinates
    if (latRegex.test(latitude) && lngRegex.test(longitude)) {
      return {
        type: 'Point',
        coordinates: [parseFloat(longitude), parseFloat(latitude)],
      };
    } else {
      return {
        type: 'Point',
        coordinates: [requestFormData.location.longitude, requestFormData.location.latitude],
      };
    }
  });
  const center =
    latRegex.test(latitude) && lngRegex.test(longitude)
      ? [parseFloat(latitude), parseFloat(longitude)]
      : [parseFloat(requestFormData.location.latitude), parseFloat(requestFormData.location.longitude)];
  const [coords, setCoords] = useState(latitude && longitude ? [latitude, longitude] : [9999, 9999]); //Initial coordinates will be off the map
  const mapRef = useRef();
  const featureGroupRef = useRef();
  const layersControlRef = useRef();

  //Fix to leaflet rendering bug
  const RefreshMap = () => {
    const map = useMap();
    useEffect(() => {
      map.attributionControl.setPrefix(leafletAttribution);
      map.invalidateSize();
    }, [map]);

    return null;
  };

  const handleCoordChange = (e) => {
    if (
      latRegex.test(latitude) &&
      lngRegex.test(longitude) &&
      (latRegex.test(e.target.value) || lngRegex.test(e.target.value))
    ) {
      setCoords(e.target.name === 'dredgeDisposalLatitude' ? [e.target.value, longitude] : [latitude, e.target.value]);
      handleChange(e);
    }
  };

  const handleCoordBlur = (e) => {
    if (
      latRegex.test(latitude) &&
      lngRegex.test(longitude) &&
      (latRegex.test(e.target.value) || lngRegex.test(e.target.value))
    ) {
      e.target.name === 'dredgeDisposalLatitude'
        ? setValue('dredgeDisposalLatitude', formatCoordStr(e.target.value))
        : setValue('dredgeDisposalLongitude', formatCoordStr(e.target.value));
    }
  };

  useEffect(() => {
    if (projectBoundaryGeometry) {
      if (projectBoundaryGeometry?.type === 'Point') {
        const markerCentroid = projectBoundaryGeometry.coordinates;
        const point = turf.point([markerCentroid[0], markerCentroid[1]]);
        const radius = turf.buffer(point, 5, { units: 'miles' });
        const bbox = turf.bbox(radius);
        const bounds = L.latLngBounds([
          { lat: bbox[1], lng: bbox[0] },
          { lat: bbox[3], lng: bbox[2] },
        ]);
        setProjectBounds(() => bounds);
      } else {
        const bbox = turf.bbox(projectBoundaryGeometry);
        const bounds = L.latLngBounds([
          { lat: bbox[1], lng: bbox[0] },
          { lat: bbox[3], lng: bbox[2] },
        ]);
        setProjectBounds(() => bounds);
      }
    }
  }, [projectBoundaryGeometry]);

  useEffect(() => {
    //Ensuring mapRef.current is initialized before calling flyToBounds()
    if (projectBounds && mapRef.current) {
      mapRef.current.flyToBounds(projectBounds);
      //Ensuring zoom is initialized
      if (mapRef.current.getZoom() === 0) {
        mapRef.current.setZoom(zoom);
      }
    }
  }, [projectBounds, zoom]);

  const markerEventHandlers = {
    dragend(event) {
      const newLatLng = event.target.getLatLng();
      setCoords(newLatLng);
      mapRef.current.flyTo(newLatLng);
      setValue('dredgeDisposalLatitude', newLatLng.lat.toFixed(7), { shouldValidate: true });
      setValue('dredgeDisposalLongitude', newLatLng.lng.toFixed(7), { shouldValidate: true });
      handleChange();
    },
  };

  const handleMarkerCreation = (e) => {
    const { layer } = e;
    const newLatLng = layer.getLatLng();

    //Setting custom marker coordinates to match user selection
    setCoords(newLatLng);
    mapRef.current.flyTo(newLatLng);
    //Setting longitude and latitude fields to match user selection
    setValue('dredgeDisposalLatitude', newLatLng.lat.toFixed(7), { shouldValidate: true });
    setValue('dredgeDisposalLongitude', newLatLng.lng.toFixed(7), { shouldValidate: true });
    handleChange();

    setProjectBoundaryGeometry(layer.toGeoJSON().geometry);

    //Removing initial layer that leaflet sets; avoids multiple markers
    e.layer.remove();
  };

  const handleMarkerDeletion = () => {
    //Setting custom marker coordinates to location off the map
    setCoords([9999, 9999]);
    //Setting latitude and longitude inputs to default empty value
    setValue('dredgeDisposalLatitude', null, { shouldValidate: true });
    setValue('dredgeDisposalLongitude', null, { shouldValidate: true });
    handleChange();
  };

  const UpdateMarkerByClick = () => {
    useMapEvents({
      click(e) {
        setCoords(e.latlng);
        mapRef.current.flyTo(e.latlng);
        setValue('dredgeDisposalLatitude', e.latlng.lat.toFixed(7), { shouldValidate: true });
        setValue('dredgeDisposalLongitude', e.latlng.lng.toFixed(7), { shouldValidate: true });
        handleChange();
      },
    });
    return null;
  };

  const DraggableMarker = () => (
    <Marker position={coords} draggable={true} autoPan={true} eventHandlers={markerEventHandlers}></Marker>
  );

  return (
    <>
      <div>
        <MapContainer
          style={{ flex: 1, width: '100%', height: height }}
          center={center}
          zoom={zoom}
          ref={mapRef}
          sleepNote={false}
          hoverToWake={false}
          sleepOpacity={1}
          worldCopyJump={true}
        >
          <FullscreenControl />
          <LayersControl ref={layersControlRef} position='bottomright'>
            <LayersControl.BaseLayer checked name='OSM Streets'>
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name='ESRI World Imagery'>
              <TileLayer
                attribution='Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
                url='https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name='USGS World Imagery (zoom limit)'>
              <TileLayer
                attribution='Tiles courtesy of the <a href="https://usgs.gov/">U.S. Geological Survey</a>'
                url='https://basemap.nationalmap.gov/arcgis/rest/services/USGSImageryOnly/MapServer/tile/{z}/{y}/{x}'
                maxZoom={16}
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name='USGS Topographic (zoom limit)'>
              <TileLayer
                attribution='Tiles courtesy of the <a href="https://usgs.gov/">U.S. Geological Survey</a>'
                url='https://basemap.nationalmap.gov/arcgis/rest/services/USGSTopo/MapServer/tile/{z}/{y}/{x}'
                maxZoom={16}
              />
            </LayersControl.BaseLayer>
          </LayersControl>
          <FeatureGroup ref={featureGroupRef}>
            {!isReadOnly && (
              <EditControl
                position='topleft'
                onCreated={handleMarkerCreation}
                onDeleted={handleMarkerDeletion}
                draw={{
                  marker: {
                    icon: new L.Icon.Default(),
                  },
                  circle: false,
                  circlemarker: false,
                  rectangle: false,
                  polyline: false,
                  polygon: false,
                }}
                edit={{
                  edit: false,
                  remove: true,
                }}
              />
            )}
            <UpdateMarkerByClick />
            <DraggableMarker />
          </FeatureGroup>
          <RefreshMap />
        </MapContainer>
      </div>
      <div className='grid-row grid-gap-1'>
        <div className='grid-col-6'>
          <TextInput
            maxLength='12'
            name='dredgeDisposalLatitude'
            label='Latitude'
            readOnly={isReadOnly}
            required
            onChange={handleCoordChange}
            onBlur={handleCoordBlur}
          />
        </div>
        <div className='grid-col-6'>
          <TextInput
            maxLength='12'
            name='dredgeDisposalLongitude'
            label='Longitude'
            readOnly={isReadOnly}
            required
            onChange={handleCoordChange}
            onBlur={handleCoordBlur}
          />
        </div>
      </div>
    </>
  );
});

export default PermitMap;
